import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useMemo, useState } from 'react';
import usePoColumns from 'src/assets/static/new_receipt/usePoColumns';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import SearchInput from 'src/components/SearchInput';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useCompany from 'src/hooks/useCompany';
import usePermission from 'src/hooks/usePermissions';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import useRedirect from 'src/hooks/useRedirect';
import useStep from 'src/hooks/useStep';
import useReceiptPoAssignmentApi from 'src/services/useReceiptPoAssignmentApi';
import { IPO } from 'src/types/PO';
import { IReceipt } from 'src/types/Receipt';
import PoGrid from '../enter_receipt/PoGrid';
import ReceiptGrid from '../enter_receipt/ReceiptGrid';

const ChangePoAssignment = () => {
  const { receiptData, refreshPage } = useReceiptDetails();
  const { changeReceiptPoAssignment, isLoading } = useReceiptPoAssignmentApi();
  const { changePoAssignment: changePoAssignmentButtonState, receiptStatus } = receiptData;
  const { getUrl, redirect } = useRedirect();
  const { company: selectedCompany } = useCompany();
  const [currentStep, helpers] = useStep(2);
  const { goToNextStep, goToPrevStep, setStep } = helpers;
  const [openAssignPoModal, setOpenAssignPoModal] = useState(false);
  const poColumns = usePoColumns();

  const [searchPo, setSearchPo] = useState<{ value: string; disable: boolean }>({
    value: '',
    disable: false,
  });

  const [supplierSearch, setSupplierSearch] = useState<string>('');
  const [lineItemSearch, setLineItemSearch] = useState<string>('');
  const [selectedReceiptPoRow, setSelectedReceiptPoRow] = useState<IPO | null>(null);
  const [, setSelectedReceiptRow] = useState<IReceipt | null>(null);
  const { isDisableNext, message } = useMemo(() => {
    const isDisableNext = !selectedReceiptPoRow?.selected;

    let message = '';

    if (isDisableNext) {
      message = 'Please select a PO.';
    }
    return {
      isDisableNext,
      message,
    };
  }, [selectedReceiptPoRow?.selected]);

  const canChangePoAssignment = usePermission({ permissions: ['CanChangePoAssignment'] });

  const disableButtonMessage = useMemo(() => {
    let message = '';
    if (!canChangePoAssignment) {
      message = "You don't have the permission to change PO";
    }
    return message;
  }, [canChangePoAssignment]);

  const toggleAssignPoModal = useCallback(() => {
    setOpenAssignPoModal((prev) => !prev);
    setSearchPo((prev) => ({ ...prev, value: '', disable: false }));
    setSupplierSearch('');
    setLineItemSearch('');
    setStep(1);
  }, [setStep]);

  const handleClick = useCallback(() => {
    toggleAssignPoModal();
  }, [toggleAssignPoModal]);

  const handleAssignPo = useCallback(() => {
    changeReceiptPoAssignment({
      receiptId: receiptData.receivingReceiptId,
      poId: selectedReceiptPoRow?.poId,
    })
      .then((res) => {
        if (!res) return;
        redirect(getUrl('receiptDetails')(String(res.receiptId), String(res.poId)));
        refreshPage();
      })
      .catch(console.error)
      .finally(() => {
        toggleAssignPoModal();
      });
  }, [
    changeReceiptPoAssignment,
    getUrl,
    receiptData.receivingReceiptId,
    redirect,
    refreshPage,
    selectedReceiptPoRow?.poId,
    toggleAssignPoModal,
  ]);

  const nextStep = useCallback(() => {
    if (currentStep === 1 && !selectedReceiptPoRow?.selected) return;
    setSearchPo((prev) => ({ ...prev, disable: true }));
    goToNextStep();
  }, [currentStep, goToNextStep, selectedReceiptPoRow?.selected]);

  const goBack = useCallback(() => {
    setSearchPo((prev) => ({ ...prev, disable: false }));
    goToPrevStep();
  }, [goToPrevStep]);

  const handlePoSearch = useCallback(() => {
    setSearchPo((prev) => ({ ...prev, disable: false }));
    nextStep();
  }, [nextStep]);

  const AssignPoTitle = useMemo(
    () => (currentStep === 1 ? 'Search for new PO' : 'Receipts Related to PO'),
    [currentStep]
  );

  const isDisabled = isButtonDisabled(changePoAssignmentButtonState);

  if (receiptStatus.includes('Completed')) return <></>;

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={[...disableButtonMessage]} />
          </div>
        }
        show={isDisabled}
      >
        <Button
          onClick={handleClick}
          buttonState={changePoAssignmentButtonState}
          color="warning"
          disabled={isDisabled}
        >
          Change PO Assignment
        </Button>
      </PopUp>

      <Dialog
        width="90%"
        height="90%"
        open={openAssignPoModal}
        onClose={toggleAssignPoModal}
        title={AssignPoTitle}
        id="modal"
      >
        <div>
          <div className="mx-[-16px] mt-[-16px]">
            <nav className="h-14 shadow-md flex items-center justify-center px-4">
              <div>
                {currentStep === 1 && (
                  <PopUp
                    content={
                      <div className="m-1 max-w-[300px]">
                        <PopUpContentWrapper type="Warning" contents={[message]} />
                      </div>
                    }
                    show={!!message}
                  >
                    <Button onClick={nextStep} disabled={isDisableNext}>
                      Next
                    </Button>
                  </PopUp>
                )}
                {currentStep === 2 && (
                  <div className="flex gap-2 items-center">
                    <Button loading={isLoading} onClick={handleAssignPo}>
                      Apply to this PO
                    </Button>
                    <Button onClick={goBack} color="info">
                      Back
                    </Button>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div>
            {currentStep === 1 ? (
              <p className="mt-2">
                Use filters provided to locate a PO that you would like to associate this document
                with.
              </p>
            ) : (
              <p className="mt-2">
                Verify that a duplicate Receipt does not exist for this PO by reviewing records
                displayed below
              </p>
            )}
            <div className="p-3 grid grid-cols-3 gap-3">
              <SearchInput
                width="100%"
                title="PO #"
                value={searchPo.value}
                onChange={(e) => {
                  setSearchPo(() => ({ value: e.target.value, disable: false }));
                }}
                placeholder="Enter PO # (Optional)"
                disabled={searchPo.disable}
                onKeyDown={handlePoSearch}
              />
              {currentStep === 1 && (
                <>
                  <SearchInput
                    width="100%"
                    title="Filter PO's by Supplier Name"
                    placeholder="Enter supplier name (Optional)"
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                    }}
                  />

                  <SearchInput
                    width="100%"
                    title="Filter PO's by Item"
                    value={lineItemSearch}
                    placeholder="Enter item # or description (Optional)"
                    onChange={(e) => {
                      setLineItemSearch(e.target.value);
                    }}
                  />
                </>
              )}
            </div>
          </div>
          {currentStep === 1 && (
            <PoGrid
              poNumber={searchPo.value}
              setSelectedReceiptPoRow={setSelectedReceiptPoRow}
              companyId={selectedCompany.companyId || String(receiptData.companyId)}
              supplierSearch={supplierSearch}
              setSupplierSearch={setSupplierSearch}
              lineItemSearch={lineItemSearch}
              setLineItemSearch={setLineItemSearch}
              columns={poColumns}
              setPoSearch={setSearchPo}
              isPoAssigned={true}
              isInvoice={false}
              isDetailsPage={true}
            />
          )}
          {currentStep === 2 && (
            <ReceiptGrid
              companyId={selectedCompany.companyId || String(receiptData.companyId)}
              selectedReceiptPoRow={selectedReceiptPoRow}
              setSelectedReceiptRow={setSelectedReceiptRow}
              showSaveLineItemButton={false}
              isSelectRowRequired={false}
              isDetailsPage={true}
            />
          )}
        </div>
        <DialogActionsBar>
          <div className="flex items-center justify-end">
            <Button color="info" onClick={toggleAssignPoModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default ChangePoAssignment;
